<script lang="ts">
	import { resolve } from "$app/paths";
	import { Picture } from "@ishavskraft/shared";
	import type { AvtaleOtherDeals } from "#lib/types/avtale.js";
	import { localeRound } from "#lib/utils/math.js";
	import {
		IconOutlinedCheckmark as Checkmark,
		IconRightArrow as Arrow,
		IconHeart,
	} from "@ishavskraft/shared";
	import { t } from "./i18n.svelte";
	import { getContext } from "svelte";

	interface Props {
		deals: AvtaleOtherDeals;
	}

	let { deals }: Props = $props();
	const formatNumber = (value: number | undefined, decimals: number = 4): string => {
		if (Number(value) === 0) return "0";
		return localeRound(Number(value), { decimals, minDecimals: decimals });
	};

	const getLang = getContext<() => string>("page-lang");
	const i18n = $derived(t(getLang()));
</script>

<div
	class="mx-edge-x mb-8 tablet:mb-11 bg-[--primary-strong-10] px-4 pb-4 tablet:px-6 tablet:pb-6 pt-2 rounded-xl"
>
	<h2>{deals.title}</h2>

	<!-- Featured deals -->
	<div class="flex flex-col tablet:flex-row gap-3 mb-4">
		{#each deals.featuredAvtaler as featured (featured._key)}
			{#if featured.reference.slug.current === "spotpris"}
				<div
					class="bg-[--glod] border-[--uer] border-solid border-2 tablet:basis-1/3 rounded-xl px-1 py-1 flex flex-col"
				>
					<div
						class="w-fit px-3 py-1 mb-2 flex items-center gap-1 border-solid border-1 border-[--geitrams] bg-[--aurora-30] rounded-full"
					>
						<IconHeart filled class="color-[--geitrams] h-[14px] w-[14px]" />
						<span class="m-0">{i18n.favorite}</span>
					</div>
					<a
						href={resolve(`stromavtale/${featured.reference.slug.current}`)}
						class="block no-underline bg-[--bg] rounded-xl px-4 py-4 flex-1"
					>
						<p class="text-h2 font-bold text-[--lysning] mb-0">
							{featured.reference.title}
						</p>
						<hr class="border-t border-b-0 border-gray my-1" />
						<div class="flex flex-row items-center gap-2">
							<p class="mt-0 text-small">{featured.subtitle}</p>
							<Arrow class="shrink-0 ml-auto color-[--lysning]" />
						</div>
						<ul class="list-none m-0 p-0 *:mb-2 *:flex *:gap-2">
							<li class="font-bold">
								<Checkmark class="color-[--lysning] h-[24px] w-[24px]" />
								{formatNumber(featured.reference.paaslag)}
								øre/kWh
							</li>
							<li class="font-bold">
								<Checkmark class="color-[--lysning] h-[24px] w-[24px]" />
								{featured.reference.fastbelop}
								kr /mnd
							</li>
						</ul>
					</a>
				</div>
			{:else}
				<a
					href={resolve(`stromavtale/${featured.reference.slug.current}`)}
					class="no-underline bg-$bg rounded-xl p-4 tablet:px-4 tablet:py-3 tablet:basis-1/3"
				>
					{#if featured.image && featured.image.src}
						<Picture
							src={featured.image.src}
							srcset={featured.image.srcset}
							srcsetWebp={featured.image.srcsetWebp}
							alt={featured.image.alt}
						/>
					{/if}
					<hr class="border-t border-b-0 border-gray my-1" />
					<div>
						<div>
							<div class="flex flex-row items-center gap-2">
								<p class="text-h2 font-bold text-[--lysning] mb-0">
									{featured.reference.title}
								</p>
								<Arrow class="shrink-0 ml-auto color-[--lysning]" />
							</div>
							<p class="mt-0 text-small">{featured.subtitle}</p>
							<ul class="list-none m-0 p-0 *:mb-2 *:flex *:gap-2">
								<li class="font-bold">
									<Checkmark class="color-[--lysning] h-[24px] w-[24px]" />
									{formatNumber(featured.reference.paaslag)}
									øre/kWh
								</li>
								<li class="font-bold">
									<Checkmark class="color-[--lysning] h-[24px] w-[24px]" />
									{featured.reference.fastbelop}
									kr /mnd
								</li>
								{#if featured.reference.perk}
									<li class="font-bold">
										<Checkmark class="color-[--lysning] h-[24px] w-[24px]" />
										{featured.reference.perk}
									</li>
								{/if}
							</ul>
						</div>
					</div>
				</a>
			{/if}
		{/each}
	</div>
	<!-- oher deals -->
	<div class="flex flex-col tablet:flex-row gap-2">
		{#each deals.avtaler as avtale (avtale._key)}
			<a
				href={resolve(`stromavtale/${avtale.reference.slug.current}`)}
				class="no-underline bg-$bg rounded-xl px-2 py-4 flex-1"
			>
				<div class="flex flex-row items-center gap-2">
					<p class="text-h2 font-bold text-[--tundra] mb-0">{avtale.reference.title}</p>
					<Arrow class="shrink-0 ml-auto color-[--lysning]" />
				</div>
				<p class="mt-0 text-small">{avtale.subtitle}</p>
			</a>
		{/each}
	</div>
</div>
